import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import NumberFormat from "react-number-format";
import { toast } from "react-toastify";
import Select from "react-select";
import moment from "moment";
import { Button, Card, CardBody, Col, Input, Row, Table } from "reactstrap";
import {
  FilterDateTime,
  FilterSelect,
  ItemDropdownMenu,
  ItemTypeahead,
  ServiceThreadSpinner,
  SortHeader,
} from ".";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, filterHelpers, helpers } from "../utils";

export default function NegativeInventory(props) {
  const [negWorking, setNegWorking] = useState(false);
  const [varWorking, setVarWorking] = useState(false);
  const [refData, setRefData] = useState({});
  const [data, setData] = useState([]);
  const [sort, setSort] = useState({ field: "itemNumber", direction: "asc" });
  const [page, setPage] = useState({ number: 1, size: 25 });
  const [negativeSites, setNegativeSites] = useState([]);
  const [varianceFilters, setVarianceFilters] = useState([
    {
      filterName: "startDate",
      value: dateHelpers.formatDateForServer(moment()),
    },
    { filterName: "endDate", value: dateHelpers.formatDateForServer(moment()) },
    { filterName: "reportType", value: "detail" },
  ]);

  useEffect(() => {
    Promise.all([
      api
        .fetch("Reference/GetSiteList")
        .then((r) => ({
          sites: _.map(r.data.sites, (s) => ({
            label: s.siteWithDescription,
            value: s.siteId,
          })),
        }))
        .catch(api.catchHandler),
      api
        .fetch("Reference/GetItemClasses")
        .then((r) => ({
          itemClasses: _.map(r.data.itemClasses, (ic) => ({
            label: `${ic.itemClassCode} | ${ic.itemClassDescription}`,
            value: ic.itemClassCode,
          })),
        }))
        .catch(api.catchHandler),
    ])
      .then((r) => {
        let result = {};
        _.each(r, (x) => _.assign(result, x));
        _.assign(result, { abcCodes: constants.ABC_CODE_LIST });
        setRefData(result);
      })
      .catch(api.catchHandler);
  }, []);

  useEffect(refreshNegData, [negativeSites, sort, page]);

  function exportNegativeXLSX() {
    const payload = _.map(negativeSites, (s) => s.value);
    api
      .postGetBlob("Inventory/ExportNegativeInventoryXLSX", payload)
      .then((r) => {
        if (r.data) {
          const XLSX_FILENAME = `NegativeInventory.xlsx`;
          helpers.downloadExport(
            r.data,
            XLSX_FILENAME,
            constants.MIME_TYPES.xlsx
          );
        } else {
          toast.error(r.message || "Error downloading report");
        }
      })
      .catch(api.catchHandler);
  }

  const exportVarianceReport = () => {
    if (!filterHelpers.isStartBeforeEnd(varianceFilters)) {
      toast.warning("Start Date must be before End Date");
      return;
    }
    setVarWorking(true);
    const payload = filterHelpers.transformFilters(varianceFilters);
    const reportName =
      payload.reportType === "detail"
        ? `Inventory_Variance_${dateHelpers.monthDayAndYear(Date.now())}.xlsx`
        : `Inventory_Variance_Summary_${dateHelpers.monthDayAndYear(
          Date.now()
        )}.xlsx`;
    api
      .postGetBlob(`Report/InventoryVariance`, payload)
      .then((r) => {
        if (!r.data) {
          toast.error(r.message || "The report could not be exported.");
          return;
        }
        let XLSX_FILENAME = reportName;
        helpers.downloadExport(r.data, XLSX_FILENAME, constants.MIME_XLSX);
      })
      .catch(api.catchHandler)
      .finally(() => setVarWorking(false));
  };

  function handleCycleCountChange(value, index) {
    const dataCopy = _.slice(data);
    dataCopy[index].cycleCount = value;
    setData(dataCopy);
  }

  function handleCycleCountSubmit() {
    const payload = _.filter(data, (dt) => !_.isNil(dt.cycleCount));
    if (payload.length === 0) {
      toast.warning("No cycle counts to process");
      return;
    }
    setNegWorking(true);
    api
      .post("Inventory/CycleCount", payload)
      .then((r) => {
        if (r.data.success) {
          toast.success("Counts succesfully updated!");
          refreshNegData();
        } else {
          toast.error("Error updating counts");
          console.error(r.data.message);
          setNegWorking(false);
        }
      })
      .catch((err) => {
        api.catchHandler(err);
        setNegWorking(false);
      });
  }

  function onSortChange(field, direction) {
    if (field !== sort.field) {
      setSort({ ...sort, field: field });
    } else {
      setSort({ ...sort, direction: direction });
    }
    setPage({ ...page, number: 1 });
  }

  function onVarianceFilterChange(changedFilter) {
    const filterChanges = filterHelpers.getFilters(
      varianceFilters,
      changedFilter
    );
    setVarianceFilters(filterChanges);
  }

  function refreshNegData() {
    setNegWorking(true);
    const payload = { sites: _.map(negativeSites, (s) => s.value) };
    api
      .fetch("Inventory/GetNegative", payload)
      .then((r) => {
        if (r.data.success) {
          setData(r.data.message);
        } else {
          toast.error(r.data.message);
        }
      })
      .catch(api.catchHandler)
      .finally(() => setNegWorking(false));
  }

  function validateVarianceFilters() {
    if (!filterHelpers.getValue(varianceFilters, "startDate")) return true;
    if (!filterHelpers.getValue(varianceFilters, "endDate")) return true;
    return false;
  }

  return (
    document.title = `Negative Inventory`,
    <DesktopContainer screenName="Negative Inventory">
      <Card className="dashboard-card">
        <CardBody>
          <div className="filter-header mb-2">
            <div className="text-center text-white mb-2">
              <span className="h4">Negative Inventory Report</span>
            </div>
            <Row>
              <Col xs={3} className="h5 text-white text-end align-self-center">
                Site:
              </Col>
              <Col xs={6}>
                <Select
                  classNamePrefix="serviceThreadSelect"
                  options={refData.sites}
                  value={negativeSites}
                  onChange={(opt) => setNegativeSites(opt)}
                  isMulti
                />
              </Col>
              <Col
                xs={3}
                className={classNames({ "d-none": data.length === 0 })}
              >
                <Button
                  className="shredButton me-5"
                  onClick={handleCycleCountSubmit}
                  disabled={negWorking}
                >
                  Process Count Quantities
                </Button>
                <Button
                  title="Download as .xlsx"
                  className="shredButtonExcel"
                  onClick={exportNegativeXLSX}
                  disabled={negWorking}
                >
                  <FontAwesomeIcon icon="file-excel" size="lg" />
                </Button>
              </Col>
            </Row>
          </div>
          <Table size="sm" className="data-table w-85 mx-auto">
            <thead className="table-header">
              <tr>
                <th width="40%" style={{ paddingLeft: "1%" }}>
                  <SortHeader
                    displayName="Item"
                    field="itemNumber"
                    sortDirection={sort.direction}
                    sorted={sort.field === "itemNumber"}
                    callback={onSortChange}
                  />
                </th>
                <th width="10%">
                  <SortHeader
                    displayName="On Hand"
                    field="onHand"
                    sortDirection={sort.direction}
                    sorted={sort.field === "onHand"}
                    callback={onSortChange}
                  />
                </th>
                <th width="10%">
                  <SortHeader
                    displayName="Site"
                    field="site"
                    sortDirection={sort.direction}
                    sorted={sort.field === "site"}
                    callback={onSortChange}
                  />
                </th>
                <th width="10%">
                  <SortHeader
                    displayName="Bin #"
                    field="bin"
                    sortDirection={sort.direction}
                    sorted={sort.field === "bin"}
                    callback={onSortChange}
                  />
                </th>
                <th width="30%">Cycle Count Qty</th>
              </tr>
            </thead>
            <tbody>
              {negWorking ? (
                <tr>
                  <td colSpan={5}>
                    <ServiceThreadSpinner on={true} />
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={5} className="h5 fw-bold text-center">
                    No Data Found
                  </td>
                </tr>
              ) : (
                _.map(data, (datum, i) => {
                  return (
                    <tr
                      key={i}
                      className={`tr-${i % 2 === 0 ? "primary" : "secondary"}`}
                    >
                      <ItemDropdownMenu
                        itemNumber={datum.itemNumber}
                        itemDescription={datum.itemWithDesc}
                        attachmentCount={datum.itemAttachmentCount}
                      />
                      <td>{datum.qtyOnHand}</td>
                      <td>{datum.siteId}</td>
                      <td>{datum.bin}</td>
                      <td>
                        <NumberFormat
                          className="form-control"
                          value={datum.cycleCount}
                          onValueChange={({ floatValue }) =>
                            handleCycleCountChange(floatValue, i)
                          }
                          decimalScale={5}
                          allowNegative={false}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            <tfoot className="table-footer">
              <tr>
                <td colSpan={5} />
              </tr>
            </tfoot>
          </Table>
        </CardBody>
      </Card>
      <Card className="dashboard-card">
        <CardBody>
          <div className="filter-header">
            <div className="text-center text-white mb-2">
              <span className="h4">Inventory Variance Report</span>
            </div>
            <Row className="mb-2">
              <Col xs={3} className="h5 text-white text-end align-self-center">
                Report Type
              </Col>
              <Col xs={6} className="align-self-center">
                <Input
                  type="radio"
                  name="reportType"
                  checked={
                    filterHelpers.getValue(varianceFilters, "reportType") ===
                    "detail"
                  }
                  onChange={() => {
                    onVarianceFilterChange({
                      filterName: "reportType",
                      value: "detail",
                    });
                  }}
                />
                <span className="text-white ms-1 me-4">Detail</span>
                <Input
                  type="radio"
                  name="reportType"
                  checked={
                    filterHelpers.getValue(varianceFilters, "reportType") ===
                    "summary"
                  }
                  onChange={() => {
                    onVarianceFilterChange({
                      filterName: "reportType",
                      value: "summary",
                    });
                  }}
                />
                <span className="text-white ms-1">Summary</span>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col xs={3} className="h5 text-white text-end align-self-center">
              { !filterHelpers.getValue(varianceFilters, "startDate").length ? helpers.requiredStar() : null} Start Date
                
              </Col>
              <Col xs={6}>
                <FilterDateTime
                  filterName="startDate"
                  className="form-control"
                  value={filterHelpers.getValue(varianceFilters, "startDate")}
                  onChangeCallback={onVarianceFilterChange}
                />
              </Col>
            </Row>
            <Row className="mb-2">
              <Col xs={3} className="h5 text-white text-end align-self-center">
              { !filterHelpers.getValue(varianceFilters, "endDate").length ? helpers.requiredStar() : null} End Date
              </Col>
              <Col xs={6}>
                <FilterDateTime
                  filterName="endDate"
                  className="form-control"
                  value={filterHelpers.getValue(varianceFilters, "endDate")}
                  onChangeCallback={onVarianceFilterChange}
                />
              </Col>
            </Row>
            <Row className="mb-2">
              <Col xs={3} className="h5 text-white text-end align-self-center">
                Item
              </Col>
              <Col xs={6}>
                <ItemTypeahead
                  value={filterHelpers.getValue(varianceFilters, "itemNumber")}
                  onChange={(opt) => {
                    if (opt) {
                      onVarianceFilterChange({
                        filterName: "itemNumber",
                        value: opt,
                        filteredDisplay: `Item: ${opt.label}`,
                      });
                    } else {
                      setVarianceFilters(
                        _.reject(
                          varianceFilters,
                          (x) => x.filterName === "itemNumber"
                        )
                      );
                    }
                  }}
                  includeSampleItems
                  isClearable
                />
              </Col>
            </Row>
            <Row className="mb-2">
              <Col xs={3} className="h5 text-white text-end align-self-center">
                ABC Code
              </Col>
              <Col xs={6}>
                <FilterSelect
                  filterName="abcCode"
                  options={refData.abcCodes}
                  value={filterHelpers.getValue("abcCode")}
                  onChangeCallback={onVarianceFilterChange}
                  isClearable
                />
              </Col>
            </Row>
            <Row className="mb-2">
              <Col xs={3} className="h5 text-white text-end align-self-center">
                Class
              </Col>
              <Col xs={6}>
                <FilterSelect
                  filterName="itemClassCode"
                  options={refData.itemClasses}
                  value={filterHelpers.getValue("itemClassCode")}
                  onChangeCallback={onVarianceFilterChange}
                  isClearable
                />
              </Col>
            </Row>
            <Row className="mb-2">
              <Col xs={3} className="h5 text-white text-end align-self-center">
                Site
              </Col>
              <Col xs={6}>
                <FilterSelect
                  filterName="siteId"
                  options={refData.sites}
                  value={filterHelpers.getValue("site")}
                  onChangeCallback={onVarianceFilterChange}
                  isClearable
                />
              </Col>
            </Row>
            <Row>
              <Col xs={{ offset: 4, size: 4 }}>
                <Button
                  className="shredButton w-100"
                  onClick={exportVarianceReport}
                  disabled={varWorking || validateVarianceFilters()}
                >
                  Report to Excel
                </Button>
              </Col>
            </Row>
          </div>
        </CardBody>
      </Card>
    </DesktopContainer>
  );
}
